import React, { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "@maeiz/core";
import { useAuth } from "@stores/auth";
import { useNotificationStore } from "@stores/notifications/notificationStore";
import { APP_ROLE } from "@lib/authKeys";

// Native SSE, not Pusher (owner/tenant use Pusher — customer/rider connect directly to the
// API's /{role}/sse stream, auth'd via ?token= since EventSource can't set headers). Poll as
// a safety net in case a connection silently drops without the browser noticing.
const UNREAD_POLL_INTERVAL_MS = 30_000;
const SSE_PATH = `/${APP_ROLE}/sse`;

export const NotificationStreamProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { isAuthenticated, token } = useAuth();
    const queryClient = useQueryClient();
    const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
    const sourceRef = useRef<EventSource | null>(null);

    useEffect(() => {
        if (!isAuthenticated || !token) {
            sourceRef.current?.close();
            sourceRef.current = null;
            return;
        }

        void fetchUnreadCount();

        const source = new EventSource(
            `${getApiBaseUrl()}${SSE_PATH}?token=${encodeURIComponent(token)}`
        );
        sourceRef.current = source;

        source.addEventListener("notification", () => {
            void fetchUnreadCount();
            void queryClient.invalidateQueries({ queryKey: ["notifications"] });
        });

        let pollId: number | undefined;
        const syncUnread = () => void fetchUnreadCount();
        const startPoll = () => {
            if (pollId !== undefined) return;
            pollId = window.setInterval(syncUnread, UNREAD_POLL_INTERVAL_MS);
        };
        const stopPoll = () => {
            if (pollId !== undefined) {
                clearInterval(pollId);
                pollId = undefined;
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                syncUnread();
                startPoll();
            } else {
                stopPoll();
            }
        };
        if (document.visibilityState === "visible") {
            startPoll();
        }
        document.addEventListener("visibilitychange", onVisibilityChange);

        const onOnline = () => syncUnread();
        window.addEventListener("online", onOnline);

        return () => {
            stopPoll();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("online", onOnline);
            source.close();
            sourceRef.current = null;
        };
    }, [isAuthenticated, token, queryClient, fetchUnreadCount]);

    return <>{children}</>;
};
